import { effect, inject, Injectable, signal } from '@angular/core';
import { ThemeService as CoreThemeService } from '../../../core/service/theme.service';

export type ColorMode = 'light' | 'dark' | 'system';

/** Ionic-style color theme presets (see https://ionicframework.com/docs/theming/colors). */
export type ColorThemeId =
  | 'crimson'   // Scarlet Red (app default — red + blue)
  | 'primary'   // Green
  | 'secondary' // Blue
  | 'tertiary'  // Purple
  | 'success'   // Green
  | 'warning'   // Amber
  | 'danger'    // Red
  ;

const MODE_STORAGE_KEY = 'app-color-mode';
const THEME_STORAGE_KEY = 'app-color-theme';

const DEFAULT_THEME_ID: ColorThemeId = 'crimson';

const VALID_THEME_IDS: ColorThemeId[] = ['crimson', 'primary', 'secondary', 'tertiary', 'success', 'warning', 'danger'];

@Injectable({ providedIn: 'root' })
export class ThemeService {
  /** Sequal owns the color preset (.color-theme-*). Light/dark is delegated to
   *  CoreThemeService — it owns the html.ion-palette-dark toggle and its own
   *  `themePreference` localStorage key. Two services racing over the same
   *  class on startup is what previously caused the saved mode to be ignored
   *  on app launch ("starts pink, fixes itself after a setting click"). */
  private readonly core = inject(CoreThemeService);
  private colorThemeId: ColorThemeId = DEFAULT_THEME_ID;

  /** Reactive light/dark, kept in sync with CoreThemeService. */
  readonly isDarkMode = signal<boolean>(false);
  /** Reactive copy of the selected color preset so headers update on change. */
  readonly colorTheme = signal<ColorThemeId>(DEFAULT_THEME_ID);

  constructor() {
    // Migrate any previously written mode key into CoreThemeService so users who
    // already set Light/Dark via Settings (before this refactor) keep that choice.
    const legacyMode = localStorage.getItem(MODE_STORAGE_KEY) as ColorMode | null;
    if (legacyMode === 'light' || legacyMode === 'dark' || legacyMode === 'system') {
      this.core.setThemePreference(legacyMode);
      localStorage.removeItem(MODE_STORAGE_KEY);
    }

    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY) as ColorThemeId | null;
    if (storedTheme && VALID_THEME_IDS.includes(storedTheme)) {
      this.colorThemeId = storedTheme;
    }
    this.applyColorTheme();

    // Keep our `isDarkMode` signal in lockstep with the core service. The effect
    // runs on construction and on every dark-mode change, so the header icon
    // (sun/moon) and any other consumers reactively follow.
    effect(() => {
      this.isDarkMode.set(this.core.isDarkMode());
    });
  }

  getMode(): ColorMode {
    // Reflect the effective preference from CoreThemeService — this includes the
    // env-configured first-visit default, so the Settings dropdown shows the real
    // starting mode (e.g. Dark) instead of always 'system'. A user choice, once
    // made, is persisted by Core and wins on every subsequent launch.
    return this.core.preference;
  }

  setMode(mode: ColorMode): void {
    this.core.setThemePreference(mode);
  }

  getColorTheme(): ColorThemeId {
    return this.colorThemeId;
  }

  setColorTheme(themeId: ColorThemeId): void {
    if (!VALID_THEME_IDS.includes(themeId)) return;
    this.colorThemeId = themeId;
    localStorage.setItem(THEME_STORAGE_KEY, themeId);
    this.applyColorTheme();
  }

  private applyColorTheme(): void {
    const root = document.documentElement;
    VALID_THEME_IDS.forEach(id => root.classList.remove(`color-theme-${id}`));
    root.classList.add(`color-theme-${this.colorThemeId}`);
    this.colorTheme.set(this.colorThemeId);
  }
}
